import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Drawer,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useEffect, useState } from 'react';
import { GoogleAssetsContainer } from '../../../../utils/constants/google';
import {
  googleTestFunction,
  fetchGoogleLocationSuggestions,
} from '../../../../services/googleAds';
import CircularLoading from '../../../CircularLoading';
import { DEFAULT_COLOR_THEME } from '../../../../utils';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { useDispatch } from 'react-redux';
import { toggleAlert } from '../../../../redux/actions/ToggleAlert';
import {
  createBrand,
  editBrand,
  getBrandsLocations,
} from '../../../../services/brand';
import { Brand } from '../../../../types/IBrand';

interface MultiUnitIndexProps {
  brand: any;
  brandPhoneNumbers: string[];
  brandUrls: string[];
}

const MultiUnitIndex: React.FC<MultiUnitIndexProps> = ({
  brand,
  brandPhoneNumbers,
  brandUrls,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [locationUsage, setLocationUsage] = useState<string>('State');
  const [locationSet, setLocationSet] = useState<any[]>([]);
  const [brandLocations, setBrandLocations] = useState<any[]>([]);
  const [sideBarOpen, setSidebarOpen] = useState<boolean>(false);
  const [sideBarData, setSidebarData] = useState<any>(null);
  const [csvUploaded, setCsvUploaded] = useState<boolean>(false);
  const [csvValue, setCsvValue] = useState<string>('');
  const [csvData, setCsvData] = useState<{
    updated: any[];
    newLocations: any[];
    noChanges: any[];
  }>({
    updated: [],
    newLocations: [],
    noChanges: [],
  });
  const phoneKeys = brandPhoneNumbers.map((phone: any) => {
    return phone.label;
  });
  const urlKeys = brandUrls.map((url: any) => {
    return url.label;
  });

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    dispatch(
      toggleAlert({
        toggle: true,
        message: 'Copied to clipboard',
        type: 'success',
      }),
    );
  };

  const fetchAllBrandLocations = async (forced: boolean = false) => {
    const locations = await getBrandsLocations(brand._id);
    const mappedlocations = locations.map((location: any) => {
      let addedKeys: {
        [key: string]: any;
      } = {};

      phoneKeys.forEach((key: string) => {
        addedKeys[`${key}_Number`] = brandPhoneNumbers.find(
          (phone: any) => phone.label === key,
        );
      });

      urlKeys.forEach((key: string) => {
        addedKeys[`${key}_URL`] = brandUrls.find(
          (url: any) => url.label === key,
        );
      });

      const gacc = location.googleAccount;
      addedKeys['googleAccount'] = gacc
        ? gacc
        : {
            phone_numbers: gacc?.phone_numbers || brandPhoneNumbers,
            landing_page_url: gacc?.landing_page_url || brandUrls,
          };

      return {
        ...location,
        ...addedKeys,
      };
    });
    if (brandLocations.length === 0 || forced) {
      setBrandLocations(
        mappedlocations.sort(
          //sort by brandId
          (a: any, b: any) => a.brandId - b.brandId,
        ),
      );
    }

    return mappedlocations;
  };

  const downloadCSV = () => {
    const locationtype: {
      [key: string]: string | number;
    } = {};
    locationtype[locationUsage] = '';
    if (locationUsage === 'Address') {
      locationtype['Radius'] = 0;
    }

    const csvData = brandLocations.map((location) => {
      let additionalKeys: any = {};
      phoneKeys.forEach((key: string) => {
        additionalKeys[`${key}_Number`] =
          location[`${key}_Number`]?.number || '';
      });
      urlKeys.forEach((key: string) => {
        additionalKeys[`${key}_URL`] = location[`${key}_URL`]?.url || '';
      });

      return {
        ID: location.brandId,
        Name: location.brand || '',
        ...additionalKeys,
        ...locationtype,
      };
    });
    if (locationUsage === 'Address') {
      locationtype[locationUsage] = locationUsage;
      locationtype['Radius'] = 'Radius';
    } else {
      locationtype[locationUsage] = locationUsage;
    }

    // Add headers to the CSV data
    csvData.unshift({
      ID: 'ID',
      Name: 'Name',
      ...phoneKeys.reduce((acc: any, key: string) => {
        acc[`${key}_Number`] = `${key}_Number`;
        return acc;
      }, {}),
      ...urlKeys.reduce((acc: any, key: string) => {
        acc[`${key}_URL`] = `${key}_URL`;
        return acc;
      }, {}),
      ...locationtype,
    });

    // const csvContent =
    //   'data:text/csv;charset=utf-8,' +
    //   ['ID', 'Name', 'Phone Number', 'URL']
    //     .concat(csvData.map((e) => Object.values(e).join(',')))
    //     .join('\n');

    const csvContent = `data:text/csv;charset=utf-8,${csvData
      .map((e) => Object.values(e).join(','))
      .join('\n')}`;

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      brand.brand.replace(
        //replace special characters and spaces with _
        /[^\w\s]/gi,
        '_',
      ) + ' units.csv',
    );
    document.body.appendChild(link); // Required for FF

    link.click();
  };

  const labels: {
    [key: string]: string;
  } = {
    ID: 'brandId',
    Name: 'brand',
    'Default Number': 'Default_Number',
    Default_URL: 'Default_URL',
  };

  const setlocationqueries = async (locations: any[]) => {
    const toset = await Promise.all(
      locations.map(async (query: string) => {
        const location = await getLocationSuggestion(query);
        return location;
      }),
    );

    setLocationSet(toset);
  };

  const uploadCSV = async (file: any) => {
    setCsvUploaded(true);
    const locations = await fetchAllBrandLocations();
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result as string;

      //parse csv to json
      const keys = text
        .split('\n')[0]
        .split(',')
        .map((key) => labels[key] || key);
      const values = text
        .split('\n')
        .slice(1)
        .map((row) => row.split(','));

      const json = values
        .map((row) => {
          const obj: any = {};
          keys.forEach((key, index) => {
            obj[key] = row[index];
          });
          return obj;
        })
        .filter((row) => row.brand);

      let templocationqueries: any[] = [];
      let updates: any[] = [];
      let newLocations: any[] = json.filter(
        //check if brandId is not in locations
        (row: any) =>
          !locations.some(
            (location: any) =>
              location.brandId.toString() === row.brandId.toString(),
          ),
      );
      let noChanges: any[] = [];

      //map json to find if its in locations
      const updatedLocations = locations.map((location: any, i: number) => {
        const found = json.find(
          (row: any) => row.brandId === location.brandId.toString(),
        );
        if (found) {
          //since found match keys and values if they are not equal
          //add new values to location object
          let hasChanges = false;
          let data = {};

          Object.keys(found).forEach((key) => {
            let from = '';
            if (key.includes('_Number')) {
              from = location[key]?.number || '';
            } else if (key.includes('_URL')) {
              from = location[key]?.url || '';
            } else {
              from = location[key] || '';
            }

            if (`${from}` !== `${found[key] || ''}`) {
              hasChanges = true;

              updates.push({
                brandId: location.brandId,
                [key]: `"${from}" => "${found[key] || ''}"`,
                data: {
                  _id: location._id,
                  brand: location.brand,
                  ...data,
                  [key]: found[key],
                },
              });
            }

            if (key !== 'ID' && key !== 'existing') {
              location[key] = found[key];
              //add to updates array
            }
            if (
              ['State', 'DMA', 'Region', 'City', 'Zip', 'Address'].includes(key)
            ) {
              let locs = location[key].split('|').map(async (loc: string) => {
                templocationqueries.push(loc);
                return;
              });
            }
          });

          //setLocationSet(templocationsets);

          //if no changes found add to noChanges array
          if (!hasChanges) {
            noChanges.push(location);
          }
        }

        return location;
      });
      const updated: {
        [key: string]: any;
      } = {};
      updates.forEach((update) => {
        if (!updated[update.brandId]) {
          updated[update.brandId] = {};
        }
        let data = {
          ...updated[update.brandId].data,
          ...update.data,
        };
        updated[update.brandId] = {
          ...updated[update.brandId],
          ...update,
          data: data,
        };
      });

      setlocationqueries(templocationqueries);

      setCsvData({
        updated: Object.values(updated),
        newLocations: newLocations,
        noChanges: noChanges,
      });
      setCsvValue(null);
    };
    reader.readAsText(file);
  };

  const getLocationSuggestion = async (q: string) => {
    const { data } = await fetchGoogleLocationSuggestions({
      brandId: brand._id,
      query: q,
      ccode: 'US',
    });

    let toreturnData =
      data?.length > 0
        ? data[0]
        : {
            geoTargetConstant: {
              canonicalName: q,
              resourceName: 'Not Found',
            },
            searchTerm: q,
          };

    setLocationSet([...locationSet, toreturnData]);
    return toreturnData;
  };

  const cancelCSVUpload = () => {
    setCsvUploaded(false);
    fetchAllBrandLocations(true);
  };

  const handleSubmitCSVChanges = async () => {
    const newbrands = csvData.newLocations.map(async (location: any) => {
      const newbrand: Brand = {
        brand: location.brand,
        // Default_Number: location.Default_Number,
        // Default_URL: location.Default_URL,
        //url: location.url,
        diy: true,
        __type: 'location',
        franchisor: brand,
        agency: brand.agency,
        isMultiUnit: true,
      };

      const createdBrand = await createBrand(newbrand);

      return createdBrand;
    });

    await Promise.all(newbrands)
      .then((res) => {
        dispatch(
          toggleAlert({
            toggle: true,
            message: 'New Units Created',
            type: 'success',
          }),
        );
      })
      .catch((err) => {
        console.dir(err, { depth: null });
        dispatch(
          toggleAlert({
            toggle: true,
            message: 'Error Creating New Units',
            type: 'error',
          }),
        );
      });

    const updatedBrands = csvData.updated.map(async (location: any) => {
      let brandId = location.data._id;

      if (brandId) {
        const result = await editBrand({ ...location.data }, brandId);
        return result;
      } else {
        return null;
      }
    });

    await Promise.all(updatedBrands)
      .then((res) => {
        dispatch(
          toggleAlert({
            toggle: true,
            message: 'Units Updated',
            type: 'success',
          }),
        );
      })
      .catch((err) => {
        console.dir(err, { depth: null });
        dispatch(
          toggleAlert({
            toggle: true,
            message: 'Error Updating Units',
            type: 'error',
          }),
        );
      });

    setCsvUploaded(false);
    fetchAllBrandLocations(true);
  };

  const BrandLocationRows = (props: { row: any }) => {
    const { row } = props;
    const [open, setOpen] = React.useState(false);

    useEffect(() => {}, []);

    return (
      <>
        <TableRow>
          <TableCell>
            {isNaN(row.brandId) ? 'To Be Assigned' : row.brandId}
          </TableCell>
          <TableCell>{row.brand}</TableCell>
          <TableCell sx={{ display: 'none' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'end',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Button
                variant="outlined"
                sx={{
                  backgroundColor: '#ffffff',
                  color: DEFAULT_COLOR_THEME,
                  textTransform: 'none',
                  padding: 0,
                }}
                onClick={() => {
                  setSidebarData(row);
                  setSidebarOpen(true);
                }}
              >
                View
              </Button>

              <Button
                variant="outlined"
                sx={{
                  backgroundColor: '#ffffff',
                  color: DEFAULT_COLOR_THEME,
                  textTransform: 'none',
                  padding: 0,
                }}
                onClick={() => {}}
              >
                Edit
              </Button>

              <Button
                variant="outlined"
                sx={{
                  backgroundColor: '#ffffff',
                  color: DEFAULT_COLOR_THEME,
                  textTransform: 'none',
                  padding: 0,
                }}
                onClick={() => {}}
              >
                Archive
              </Button>
            </Box>
          </TableCell>
        </TableRow>
      </>
    );
  };

  const Sidebar = (
    <Drawer
      open={sideBarOpen}
      onClose={(e) => {
        setSidebarOpen(false);
      }}
      anchor="right"
    >
      <Box
        sx={{
          maxWidth: `400px`,
          minWidth: `400px`,
          width: `400px`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'start',
          minHeight: `100vh`,
        }}
      >
        {sideBarData && (
          <Box>
            <Typography
              variant="h6"
              fontWeight={800}
              color={DEFAULT_COLOR_THEME}
              padding={2}
              sx={{
                borderBottom: `1px solid #f0f0f0`,
              }}
            >
              {sideBarData.brand || ''}
            </Typography>
            <Box
              sx={{
                padding: 2,
              }}
            >
              <Typography>Phone Numbers:</Typography>
              {sideBarData.googleAccount &&
                sideBarData.googleAccount?.phone_numbers?.map(
                  (phone: any, i: number) => {
                    return (
                      <Box
                        key={i}
                        sx={{
                          padding: 2,
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography>{phone.label}</Typography>
                        <Typography>{phone.number}</Typography>
                      </Box>
                    );
                  },
                )}
            </Box>
            <Box
              sx={{
                padding: 2,
              }}
            >
              <Typography>URLs:</Typography>
              {sideBarData.googleAccount &&
                sideBarData.googleAccount?.landing_page_url?.map(
                  (url: any, i: number) => {
                    return (
                      <Box
                        key={i}
                        sx={{
                          padding: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          alignItems: 'start',
                        }}
                      >
                        <Typography>{url.label}</Typography>
                        <Typography
                          sx={{
                            paddingLeft: 2,
                          }}
                        >
                          {url.url}
                        </Typography>
                      </Box>
                    );
                  },
                )}
            </Box>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 2,
            borderTop: `1px solid #f0f0f0`,
            width: `100%`,
          }}
        >
          <Button variant="contained">Edit</Button>
          <Button variant="contained">Archive</Button>
        </Box>
      </Box>
    </Drawer>
  );

  useEffect(() => {
    setBrandLocations([]);
    setCsvUploaded(false);
    fetchAllBrandLocations();
    setCsvValue(null);
    setCsvData({
      updated: [],
      newLocations: [],
      noChanges: [],
    });
  }, []);

  useEffect(() => {
    if (brand.allowLocations) {
      fetchAllBrandLocations(true);
    }
  }, [brand]);
  return (
    <Box>
      <Box
        sx={{
          display: csvUploaded ? 'none' : 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Button
          variant="contained"
          sx={{
            backgroundColor: DEFAULT_COLOR_THEME,
            color: '#ffffff',
            marginBottom: 2,
          }}
          onClick={downloadCSV}
        >
          Download CSV
        </Button>

        <FormControl sx={{ minWidth: 150 }} size="small">
          <InputLabel id="demo-simple-select-label">Location Usage</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={locationUsage}
            label="Location Usage"
            onChange={(e) => {
              setLocationUsage(e.target.value as string);
            }}
          >
            <MenuItem value={'State'}>State</MenuItem>
            <MenuItem value={'DMA'}>DMA</MenuItem>
            <MenuItem value={'City'}>City</MenuItem>
            <MenuItem value={'Zip'}>Zip</MenuItem>
            <MenuItem value={'Region'}>Region</MenuItem>
            <MenuItem value={'Address'}>Address</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="contained"
          sx={{
            backgroundColor: DEFAULT_COLOR_THEME,
            color: '#ffffff',
            marginBottom: 2,
          }}
          component="label"
          onClick={() => {
            setCsvValue(null);
          }}
        >
          Upload CSV
          <input
            type="file"
            hidden
            accept=".csv"
            value={csvValue}
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                uploadCSV(e.target.files[0]);
              }
            }}
          />
        </Button>
      </Box>
      <Box
        sx={{
          display: csvUploaded ? 'flex' : 'none',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Button
          variant="outlined"
          sx={{
            flexShrink: 0,
            marginBottom: 2,
          }}
          onClick={() => {
            cancelCSVUpload();
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{
            backgroundColor: DEFAULT_COLOR_THEME,
            color: '#ffffff',
            marginBottom: 2,
          }}
          onClick={() => {
            handleSubmitCSVChanges();
          }}
        >
          Apply Changes
        </Button>
      </Box>

      <Box
        sx={{
          display: csvUploaded ? 'none' : 'flex',
        }}
      >
        <TableContainer component={Paper}>
          <Table aria-label="collapsible table" size="small">
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: '#096f4d',
                }}
              >
                <TableCell sx={{ color: '#ffffff' }}>ID</TableCell>
                <TableCell sx={{ color: '#ffffff' }}>Name</TableCell>
                <TableCell
                  sx={{ color: '#ffffff', textAlign: 'right', display: 'none' }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {brandLocations.map((row, i) => (
                <BrandLocationRows key={`${row.group} ${i}`} row={row} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box
        sx={{
          display: csvUploaded ? 'block' : 'none',
          textAlign: 'center',
        }}
      >
        {csvData.newLocations.length > 0 && (
          <>
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Typography
                  variant="h6"
                  fontWeight={800}
                  color={DEFAULT_COLOR_THEME}
                >
                  New Units ({csvData.newLocations.length}):
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid
                  container
                  sx={{
                    paddingX: 2,
                    marginBottom: 4,
                  }}
                >
                  {csvData.newLocations.map((location: any, i: number) => {
                    return (
                      <Grid
                        item
                        xs={12}
                        md={6}
                        xl={4}
                        key={i}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',

                          padding: 2,
                        }}
                      >
                        <Box
                          sx={{
                            backgroundColor: DEFAULT_COLOR_THEME,
                            padding: 1,
                            borderRadius: `5px 5px 0 0`,
                            color: '#ffffff',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                          }}
                        >
                          <Typography
                            sx={{ fontWeight: 800, display: 'inline' }}
                          >
                            {location.brand || ''}
                          </Typography>
                          <Typography
                            sx={{ fontWeight: 800, display: 'inline' }}
                          >
                            ID: {location.brandId || 'To Be Assigned'}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            paddingX: 2,
                            paddingY: 2,
                            backgroundColor: '#f0f0f0',
                            borderRadius: `0 0 5px 5px`,
                            maxHeight: `200px`,
                            overflowY: `auto`,
                          }}
                        >
                          {Object.keys(location).map((key, i) => {
                            return (
                              <Box
                                key={i}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  gap: 2,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 800,
                                    display: 'inline',
                                  }}
                                >
                                  {key}:
                                </Typography>
                                <Typography
                                  sx={{
                                    fontWeight: 800,
                                    display: 'inline',
                                    color: `#335533`,
                                  }}
                                >
                                  "{location[key]}"
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </>
        )}

        {csvData.updated.length > 0 && (
          <>
            <Accordion defaultExpanded={csvData.newLocations.length === 0}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel2-content"
                id="panel2-header"
              >
                <Typography
                  variant="h6"
                  fontWeight={800}
                  color={DEFAULT_COLOR_THEME}
                >
                  Updated Units ({csvData.updated.length}):
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid
                  container
                  sx={{
                    paddingX: 2,
                    marginBottom: 4,
                  }}
                >
                  {csvData.updated.map((location: any, i: number) => {
                    return (
                      <Grid
                        item
                        xs={12}
                        md={6}
                        xl={4}
                        key={i}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          padding: 2,
                        }}
                      >
                        <Box
                          sx={{
                            backgroundColor: DEFAULT_COLOR_THEME,
                            padding: 1,
                            borderRadius: `5px 5px 0 0`,
                            color: '#ffffff',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                          }}
                        >
                          <Typography
                            sx={{ fontWeight: 800, display: 'inline' }}
                          >
                            {location?.data?.brand || ''}
                          </Typography>
                          <Typography
                            sx={{ fontWeight: 800, display: 'inline' }}
                          >
                            ID: {location.brandId || 'To Be Assigned'}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            paddingX: 2,
                            paddingY: 2,
                            backgroundColor: '#f0f0f0',
                            borderRadius: `0 0 5px 5px`,
                            maxHeight: `200px`,
                            overflowY: `auto`,
                          }}
                        >
                          <Typography textAlign={'left'}>Changes:</Typography>
                          {Object.keys(location).map(
                            (key: string, i: number) => {
                              if (['brandId', 'data'].includes(key))
                                return null;
                              return (
                                <Box
                                  key={i}
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                  }}
                                >
                                  <Box
                                    key={i}
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'row',
                                      gap: 2,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontWeight: 800,
                                        display: 'inline',
                                      }}
                                    >
                                      {key}:
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontWeight: 800,
                                        display: 'inline',
                                        color: `#335533`,
                                      }}
                                    >
                                      {location[key]}
                                    </Typography>
                                  </Box>
                                  {[
                                    'State',
                                    'DMA',
                                    'Region',
                                    'City',
                                    'Zip',
                                    'Address',
                                  ].includes(key) && (
                                    <>
                                      <Box
                                        sx={{
                                          display: 'block',
                                          textAlign: 'left',
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontWeight: 800,
                                            display: 'inline',
                                          }}
                                        >
                                          {key}:
                                        </Typography>
                                        {location.data[key]
                                          .split('|')
                                          .map((item: string, i: number) => (
                                            <>
                                              <Box>
                                                {locationSet
                                                  .filter(
                                                    (geoLoc: any) =>
                                                      geoLoc?.searchTerm ===
                                                      item,
                                                  )
                                                  .map((geoLoc) => (
                                                    <>
                                                      {geoLoc ? (
                                                        <>
                                                          <Typography
                                                            sx={{
                                                              fontWeight: 800,
                                                              paddingLeft: 2,
                                                            }}
                                                          >
                                                            {
                                                              geoLoc
                                                                .geoTargetConstant
                                                                .canonicalName
                                                            }
                                                          </Typography>
                                                          <Typography
                                                            sx={{
                                                              fontWeight: 400,
                                                              fontStyle:
                                                                'italic',
                                                              paddingLeft: 4,
                                                              fontSize:
                                                                '0.7rem',
                                                            }}
                                                          >
                                                            {
                                                              geoLoc
                                                                .geoTargetConstant
                                                                .resourceName
                                                            }
                                                          </Typography>
                                                        </>
                                                      ) : (
                                                        <>
                                                          <Typography
                                                            sx={{
                                                              fontWeight: 800,
                                                              paddingLeft: 2,
                                                            }}
                                                          >
                                                            {item}
                                                          </Typography>
                                                          <Typography
                                                            sx={{
                                                              fontWeight: 400,
                                                              fontStyle:
                                                                'italic',
                                                              paddingLeft: 4,
                                                              fontSize:
                                                                '0.7rem',
                                                            }}
                                                          >
                                                            {key} Not Found
                                                          </Typography>
                                                        </>
                                                      )}
                                                    </>
                                                  ))}
                                              </Box>
                                            </>
                                          ))}
                                      </Box>
                                    </>
                                  )}
                                </Box>
                              );
                            },
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </>
        )}

        {csvData.noChanges.length > 0 && (
          <>
            <Accordion
              defaultExpanded={
                csvData.updated.length === 0 &&
                csvData.newLocations.length === 0
              }
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel2-content"
                id="panel2-header"
              >
                <Typography
                  variant="h6"
                  fontWeight={800}
                  color={DEFAULT_COLOR_THEME}
                >
                  Not Changed ({csvData.noChanges.length}):
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid
                  container
                  sx={{
                    paddingX: 2,
                    marginBottom: 4,
                  }}
                >
                  {csvData.noChanges.map((location: any, i: number) => {
                    return (
                      <Grid
                        item
                        xs={12}
                        md={6}
                        xl={4}
                        key={i}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          padding: 2,
                        }}
                      >
                        <Box
                          sx={{
                            backgroundColor: DEFAULT_COLOR_THEME,
                            padding: 1,
                            borderRadius: `5px 5px 0 0`,
                            color: '#ffffff',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                          }}
                        >
                          <Typography
                            sx={{ fontWeight: 800, display: 'inline' }}
                          >
                            {location.brand || ''}
                          </Typography>
                          <Typography
                            sx={{ fontWeight: 800, display: 'inline' }}
                          >
                            ID: {location.brandId || 'To Be Assigned'}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            paddingX: 2,
                            paddingY: 2,
                            backgroundColor: '#f0f0f0',
                            borderRadius: `0 0 5px 5px`,
                            maxHeight: `200px`,
                            overflowY: `auto`,
                          }}
                        >
                          {Object.keys(location).map((key, i) => {
                            return (
                              ['brand', 'brandId', 'phoneNumber'].includes(
                                key,
                              ) && (
                                <Box
                                  key={i}
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: 2,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: 800,
                                      display: 'inline',
                                    }}
                                  >
                                    {key}:
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontWeight: 800,
                                      display: 'inline',
                                      color: `#335533`,
                                    }}
                                  >
                                    "{JSON.stringify(location[key])}"
                                  </Typography>
                                </Box>
                              )
                            );
                          })}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Box>

      {Sidebar}
    </Box>
  );
};

export default MultiUnitIndex;
